import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAuth, signOut } from 'firebase/auth';
import { Layout, Message } from "@arco-design/web-react";
import Content from "@arco-design/web-react/es/Layout/content";
import Footer from "@arco-design/web-react/es/Layout/footer";
import Header from "@arco-design/web-react/es/Layout/header";
import SendbirdProvider from "@sendbird/uikit-react/SendbirdProvider";

import { BaseMenu } from "./BaseMenu";
import { SendbirdUnreadFallback, SendbirdUnreadProvider } from "./SendbirdUnreadContext";
import { FooterContent } from "./FooterContent/FooterContent";
import { routes } from "../routes/routes";
import { RootState } from "../store/store";
import { clearUser } from '../store/userSlice';
import { getBackgroundColor, getHeaderColor } from "../utils/cssVariables";
import { useAutoLogout } from '../utils/hooks/useAutoLogout';
import { useFetchConfig } from "../api/hooks/useFetchConfig";
import { usePrefetchPublishedBanner } from "../api/hooks/usePublishedBanner";
import { isPublicProfilePath } from "../utils/publicProfilePath";
import { inferThemeKeyFromHostname } from "../theme";
import { useAiMatchingPreview } from "../hooks/useAiMatchingPreview";
import { sendbirdStringSet } from "../utils/sendbirdStringSet";

export const AppLayout = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isConfigLoading } = useFetchConfig();

  const logOutInactiveUser = () => {
    const auth = getAuth();
    signOut(auth)
      .then(() => {
        dispatch(clearUser());
        localStorage.removeItem('lastActive');
        Message.success({ content: t('notification_messages.logged_out_inactivity'), closable: true, duration: 3000 });
        setTimeout(() => {
          navigate(routes.LOGIN);
        }, 10);
      })
      .catch((error) => {
        console.error("Logout failed due to inactivity:", error);
      });
  };

  const user = useSelector((state: RootState) => state.user.user);
  usePrefetchPublishedBanner(user?.id);
  const sendbirdAppIdFromConfig = useSelector((state: RootState) => state.config.sendbirdAppId);
  const isTenantHost = Boolean(inferThemeKeyFromHostname());
  // Wait for /config on tenant hosts — never fall back to platform REACT_APP_SENDBIRD during load or on failure.
  const resolvedSendbirdAppId =
    !isConfigLoading
      ? sendbirdAppIdFromConfig ||
        (!isTenantHost ? process.env.REACT_APP_SENDBIRD_APP_ID : "") ||
        ""
      : "";

  const sendbirdUserId = user?.id ? String(user.id).trim() : "";
  const canMountSendbird = Boolean(resolvedSendbirdAppId && sendbirdUserId);

  const aiMatchingPreview = useAiMatchingPreview();
  const isVersionPage = location.pathname === routes.VERSION || location.pathname.startsWith(`${routes.VERSION}/`);
  const isIntakePage = aiMatchingPreview && location.pathname === routes.INTAKE;
  const isAuthFullBleed =
    aiMatchingPreview &&
    (location.pathname === routes.LOGIN ||
      location.pathname === routes.REGISTER);

  const isMentorProfilePage =
    location.pathname.startsWith("/mentor/") || location.pathname.startsWith("/mentee/");

  const isDashboardPage =
    location.pathname === routes.DASHBOARD ||
    location.pathname === routes.ADMIN_DASHBOARD;

  const useCreamWideLayout =
    aiMatchingPreview &&
    (isMentorProfilePage || isDashboardPage || isIntakePage);

  const authOrVersionPage =
    location.pathname === routes.LOGIN ||
    location.pathname === routes.REGISTER ||
    location.pathname === routes.RESETPASSWORD ||
    location.pathname === routes.CONFIRM_EMAIL ||
    location.pathname === routes.CONFIRM_RESET_PASSWORD ||
    isVersionPage ||
    isIntakePage;

  // Show header/footer immediately — SendbirdUnreadFallback covers menu until provider is ready.
  const showAppChrome = !authOrVersionPage;

  useAutoLogout(logOutInactiveUser, !!user && !isIntakePage);

  useEffect(() => {
    const isLoggedIn = !!user && user.id !== 0;
    const isAuthPage =
      location.pathname === routes.LOGIN ||
      location.pathname === routes.REGISTER ||
      location.pathname === routes.RESETPASSWORD ||
      location.pathname === routes.CONFIRM_EMAIL ||
      location.pathname === routes.CONFIRM_RESET_PASSWORD ||
      location.pathname === routes.INTAKE;

    if (isLoggedIn || isAuthPage || isPublicProfilePath(location.pathname)) {
      return;
    }

    navigate(routes.LOGIN);
  }, [location.pathname, navigate, user]);

  const shouldAlignCenter =
    location.pathname === routes.LOGIN ||
    location.pathname === routes.REGISTER ||
    location.pathname === routes.RESETPASSWORD ||
    location.pathname === routes.CONFIRM_RESET_PASSWORD;

  const isMessagesRoute = location.pathname === routes.MESSAGES;

  const messagesFlexLayout = isMessagesRoute
    ? { display: 'flex' as const, flexDirection: 'column' as const, flex: 1, minHeight: 0 }
    : {};

  const authShellStyle = isAuthFullBleed
    ? {
        width: "100%",
        minHeight: "100vh",
        height: "100%",
        flex: 1,
        display: "flex",
        flexDirection: "column" as const,
        overflow: "hidden",
      }
    : undefined;

  const contentStyle = isAuthFullBleed || isIntakePage
    ? {
        textAlign: "left" as const,
        padding: 0,
        flex: 1,
        width: "100%",
        minHeight: 0,
      }
    : isVersionPage
      ? {
          textAlign: "left" as const,
          alignContent: shouldAlignCenter ? "center" as const : undefined,
          overflow: "auto" as const,
          flex: 1,
        }
      : isMessagesRoute
        ? {
            textAlign: "left" as const,
            alignContent: shouldAlignCenter ? "center" as const : undefined,
            ...messagesFlexLayout,
            padding: 0,
          }
        : {
            textAlign: "center" as const,
            alignContent: shouldAlignCenter ? "center" as const : undefined,
          };

  const innerDivStyle = isAuthFullBleed
    ? undefined
    : isIntakePage
      ? {
          justifyContent: "center" as const,
          maxWidth: "none" as const,
          width: "100%",
          margin: "0 auto",
        }
      : isVersionPage
        ? { width: "100%", minHeight: "100%" }
        : isMessagesRoute
          ? {
              justifyContent: "flex-start" as const,
              maxWidth: 1000,
              width: "100%",
              margin: "0 auto",
              ...messagesFlexLayout,
            }
          : useCreamWideLayout
            ? {
                justifyContent: "center" as const,
                maxWidth: "none" as const,
                width: "100%",
                margin: "0 auto",
              }
            : {
                justifyContent: "center" as const,
                maxWidth: 800,
                width: "100%",
                margin: "0 auto",
              };

  const layoutBackground =
    isAuthFullBleed || useCreamWideLayout ? "#f5f0e8" : getBackgroundColor();

  const innerLayout = (
        <Layout
          className={isAuthFullBleed ? "auth-shell-inner" : undefined}
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            ...authShellStyle,
          }}
        >
          {showAppChrome && (
              <Header style={{ backgroundColor: getHeaderColor(), flexShrink: 0 }}>
                <BaseMenu />
              </Header>
            )}
          <Layout
            className={isAuthFullBleed ? "auth-shell-body" : undefined}
            style={{
              backgroundColor: layoutBackground,
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              ...messagesFlexLayout,
              ...authShellStyle,
            }}
          >
            <Content
              className={isAuthFullBleed ? "auth-page-content" : undefined}
              style={contentStyle}
            >
              <div
                className={isAuthFullBleed ? "auth-layout-full" : undefined}
                style={innerDivStyle}
              >
                {children}
              </div>
            </Content>
            {showAppChrome && (
                <Footer style={{ backgroundColor: getHeaderColor(), flexShrink: 0 }}>
                  <FooterContent />
                </Footer>
              )}
          </Layout>
        </Layout>
  );

  return (
    <Layout
      className={isAuthFullBleed ? "auth-shell-root" : undefined}
      style={{
        minHeight: "100vh",
        width: "100%",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        ...(isAuthFullBleed ? { overflow: "hidden" } : {}),
      }}
    >
      {canMountSendbird ? (
      <SendbirdProvider
        appId={resolvedSendbirdAppId}
        userId={sendbirdUserId}
        stringSet={sendbirdStringSet}
        uikitOptions={{
          groupChannel: {
            input: {
              camera: {
                enablePhoto: false,
                enableVideo: false,
              },
              gallery: {
                enablePhoto: false,
                enableVideo: false,
              },
              enableDocument: false,
            },
            replyType: 'none',
          }
        }}
      >
        <SendbirdUnreadProvider>{innerLayout}</SendbirdUnreadProvider>
      </SendbirdProvider>
      ) : (
        <SendbirdUnreadFallback>{innerLayout}</SendbirdUnreadFallback>
      )}
    </Layout>
  );
};
